import { useState, type ReactNode } from 'react';
import { Collapsible } from '@/components/Collapsible';
import styles from './panel.module.css';

/**
 * Collapsible section card for the A3 automation panel. Collapsed state shows
 * a one-line `summary`; expanding reveals `children`. Tasks 8-9 render the
 * per-section bodies (scope/condition/outcome, branches) inside this shell.
 *
 * Thin wrapper over the shared `Collapsible` primitive — SectionCard owns the
 * open/summary/badge presentation; the primitive owns the toggle mechanics.
 */
export function SectionCard({
  title,
  summary,
  badge,
  defaultOpen = false,
  children,
}: {
  title: string;
  summary: string;
  /** An advisory pill next to the title (e.g. a beta/preview marker). */
  badge?: string;
  defaultOpen?: boolean;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Collapsible
      open={open}
      onToggle={() => setOpen((v) => !v)}
      chevronPosition="end"
      className={styles.section}
      headerClassName={styles.sectionHeader}
      bodyClassName={styles.sectionBody}
      chevronClassName={styles.chevron}
      header={
        <>
          <span className={styles.sectionTitle}>{title}</span>
          {badge && <span className={styles.badge}>{badge}</span>}
          {!open && <span className={styles.sectionSummary}>{summary}</span>}
        </>
      }
    >
      {children}
    </Collapsible>
  );
}
